import React, { useMemo, type ReactNode } from 'react';
import { ReactComponent as CartIcon } from '../../../icons/svg/shopping-cart.svg';
import { Badge } from '../../../components/badge';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils/cn';
import { HoverCard, HoverCardContent } from '../../../components/hover-card';
import { HoverCardTrigger } from '../../../components/hover-card/hover-card-trigger';
import type { HeaderCartInfo } from './Header';
import { ShoppingCartMenu } from './ShoppingCartMenu';
import { useAtom } from 'jotai';
import { isShoppingCartMenuOpenState } from '../states/ui-shopping-cart-menu';
const _cartIconVariants = cva('size-5', {
    variants: {
        fill: {
            dark: '[&>path]:fill-gray-900!',
            // The page ink the mobile header's other icons are set in.
            ink: '[&>path]:fill-[#323232]!',
            light: '[&>path]:fill-gray-50!',
        },
    },
    defaultVariants: {
        fill: 'dark',
    },
});

interface UserCartProps extends VariantProps<typeof _cartIconVariants> {
    onUserCartClick: () => void;
    cartInfo: HeaderCartInfo;
    /**
     * Open the mini-cart when a pointer rests on the icon.
     *
     * The revamp's header opens a cart drawer on click instead, where the menu
     * is a confirmation shown after an add rather than something offered to a
     * pointer on its way somewhere else. Defaults to the hover the legacy
     * header has always had, so a caller that says nothing keeps it.
     */
    openCartMenuOnHover?: boolean;
    /** The host's own cart glyph, in place of the default bag. */
    icon?: ReactNode;
}

export const UserCart = (props: UserCartProps) => {
    const {
        fill,
        onUserCartClick,
        cartInfo,
        openCartMenuOnHover = true,
        icon,
    } = props;
    const [isShoppingCartMenuOpen, setIsShoppingCartMenuOpen] = useAtom(
        isShoppingCartMenuOpenState,
    );
    const { cartCount } = cartInfo;

    const cartIconComponent = (
        <button
            type="button"
            aria-label="Cart"
            className="relative flex items-center"
            onClick={onUserCartClick}
        >
            {/* The name belongs on the control. It sat on this div, where
                `aria-label` is ignored for want of a role, so the button's
                only name was the count digit — or nothing at all with an
                empty cart. */}
            <div className="flex items-center">
                {icon ?? (
                    <CartIcon
                        className={cn(_cartIconVariants({ fill }), 'size-6')}
                    />
                )}
            </div>
            {/* After the glyph, and lifted: a host's glyph class can carry
                a z-index of its own (the mobile header's does), and drawn
                first the count went under the icon it belongs to. */}
            <div className="absolute -top-2 -right-2 z-10 flex">
                {cartCount > 0 && (
                    <Badge
                        size="centeredCompact"
                        text={cartCount > 9 ? 'small' : 'default'}
                        // Black rather than brick or red, which read as
                        // alerts — but only on the revamp's bar. `ink` is the
                        // fill only that header passes; the legacy one, which
                        // is what every visitor sees while `revamp_2026` is
                        // off, still passes `dark` or `light` and keeps the
                        // count it has always had.
                        color={
                            fill === 'ink'
                                ? undefined
                                : fill === 'light'
                                  ? 'destructive'
                                  : 'default'
                        }
                        className={cn(
                            fill === 'ink' && 'bg-[#181818] text-white',
                        )}
                    >
                        {cartCount > 9 ? '9+' : cartCount}
                    </Badge>
                )}
            </div>
        </button>
    );

    const shoppingCartMenu = useMemo(
        () => (
            <ShoppingCartMenu
                cartInfo={cartInfo}
                onUserCartClick={onUserCartClick}
            />
        ),
        [cartInfo, onUserCartClick],
    );

    const renderDesktopCartMenu = () => (
        <HoverCard open={isShoppingCartMenuOpen}>
            {/* Where the click opens a drawer the menu is a confirmation,
                shown after an add rather than offered to a pointer passing
                over the icon on its way somewhere else. Where the click still
                navigates to the cart, the hover is how the menu is reached. */}
            <HoverCardTrigger
                onMouseEnter={
                    openCartMenuOnHover
                        ? () => setIsShoppingCartMenuOpen(true)
                        : undefined
                }
                aria-expanded={isShoppingCartMenuOpen}
                aria-haspopup="true"
                aria-label={`Shopping cart with ${cartCount} items`}
            >
                {cartIconComponent}
            </HoverCardTrigger>
            <HoverCardContent
                variant="cart"
                onMouseLeave={() => setIsShoppingCartMenuOpen(false)}
                role="dialog"
                aria-label="Shopping cart contents"
            >
                {shoppingCartMenu}
            </HoverCardContent>
        </HoverCard>
    );

    if (cartCount === 0) return null;

    return (
        <>
            <div className="hidden md:flex md:items-center">
                {renderDesktopCartMenu()}
            </div>
            <div className="flex items-center md:hidden">
                {cartIconComponent}
            </div>
        </>
    );
};
